import Cookies from 'js-cookie';

import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import WebHttpClient from '~src/httpClient/WebHttpClient';

import { AcknowledgeUserIdentityConflictsRequest } from '../serverApi/requests/AcknowledgeUserIdentityConflictsRequest';
import { GetUserIdentityConflictTotalsRequest,GetUserIdentityConflictTotalsResult } from '../serverApi/requests/GetUserIdentityConflictTotalsRequest';
import { GetUserIdentityMigrationBackgroundJobRequest, IGetUserIdentityMigrationBackgroundJobResult } from '../serverApi/requests/GetUserIdentityMigrationBackgroundJobRequest';
import { GetUserIdentityMigrationProgressRequest, GetUserIdentityMigrationProgressResult } from '../serverApi/requests/GetUserIdentityMigrationProgressRequest';
import { ReevaluateUserIdentityConflictsRequest } from '../serverApi/requests/ReevaluateUserIdentityConflictsRequest';
import { ResolveAmbiguousUserIdentityConflictRequest } from '../serverApi/requests/ResolveAmbiguousUserIdentityConflictRequest';
import { RetryUserIdentityMigrationRequest } from '../serverApi/requests/RetryUserIdentityMigrationRequest';
import { RunUserIdentityMigrationBackgroundJobNowRequest } from '../serverApi/requests/RunUserIdentityMigrationBackgroundJobNowRequest';
import { ShouldShowIdentityMigrationInfoToastRequest } from '../serverApi/requests/ShouldShowIdentityMigrationInfoToastRequest';

/**
 * This service is in charge of performing requests related to User Identity Conflicts.
 */
export namespace UserIdentityConflictsService {

  const MIGRATION_MESSAGE_DISMISS_COOKIE = {
    NAME: 'DISMISS_IDENTITY_MIGRATION_MESSAGE',
    EXPIRATION_DAYS: 2, // The policy is to show the message every two days if dismissed.
  };

  export function fetchUserIdentityMigrationBackgroundJob(): Promise<IGetUserIdentityMigrationBackgroundJobResult> {
    return WebHttpClient.sendRequest(new GetUserIdentityMigrationBackgroundJobRequest());
  }

  export function runUserIdentityMigrationBackgroundJobNow(): Promise<IResult> {
    return WebHttpClient.sendRequest(new RunUserIdentityMigrationBackgroundJobNowRequest());
  }

  export function fetchUserIdentityMigrationProgress(): Promise<GetUserIdentityMigrationProgressResult> {
    return WebHttpClient.sendRequest(new GetUserIdentityMigrationProgressRequest());
  }

  export function shouldShowIdentityMigrationInfoToast(): Promise<IResult | boolean> {
    return new Promise<IResult | boolean>((resolve, reject) => {
      if (Cookies.get(MIGRATION_MESSAGE_DISMISS_COOKIE.NAME)) {
        // If the cookie is in place, then we won't show it.
        resolve(false);
      } else {
        WebHttpClient.sendRequest(new ShouldShowIdentityMigrationInfoToastRequest()).then((result) => {
          resolve(result);
        });
      }
    });
  }

  export function disableIdentityMigrationInfoToast(): void {
    const now = new Date();
    const expiration = new Date();
    expiration.setDate(now.getDate() + MIGRATION_MESSAGE_DISMISS_COOKIE.EXPIRATION_DAYS);
    Cookies.set(MIGRATION_MESSAGE_DISMISS_COOKIE.NAME, 'true', {expires: expiration});
  }

  export function fetchUserIdentityConflictTotals(): Promise<GetUserIdentityConflictTotalsResult> {
    return WebHttpClient.sendRequest(new GetUserIdentityConflictTotalsRequest());
  }

  export function retryUserIdentityMigration(ids: string[]): Promise<IResult> {
    return WebHttpClient.sendRequest(new RetryUserIdentityMigrationRequest({ids: ids}));
  }

  export function acknowledgeUserIdentityConflicts(ids: string[]): Promise<IResult> {
    return WebHttpClient.sendRequest(new AcknowledgeUserIdentityConflictsRequest({ids: ids}));
  }

  export function reevaluateUserIdentityConflicts(ids: string[]): Promise<IResult> {
    return WebHttpClient.sendRequest(new ReevaluateUserIdentityConflictsRequest({ids: ids}));
  }

  export function resolveAmbiguousUserIdentityConflict(id: string, dataRecordId: string): Promise<IResult> {
    return WebHttpClient.sendRequest(new ResolveAmbiguousUserIdentityConflictRequest({id: id, dataRecordId: dataRecordId}));
  }
}
